import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  DrawLayerResponseDTO,
  GetOperationDetails200Response,
  OperationDeleteDTO,
  OperationDetailsResultDTO,
  OperationLocalRoleUpdatedDTO,
  OperationMemberRemoveData,
  OperationResultDTO,
  OperationTrackerDetailsDTO,
  OperationTrackerDetailsEventDTO,
  OperationUserDTO,
  SymbolDTO,
  TrackerDeleteDTO,
  UserDetailsUpdateEventDTO,
  UserResultDTO,
  UserWithConsentAndLocalRoleDTO,
  UserWithConsentNestedResultDTO,
} from '@nora-monorepo/nora-api-client';
import {
  ConsentScopeTypeEnum,
} from '@nora-monorepo/nora-api-client';
import {
  ensureValidDate,
  getUserDisplayName,
  isNonEmptyString,
  isNonZero,
} from '@nora-monorepo/shared';
import type { PositionResponse } from '@nora-monorepo/track-api-web-client';
import {
  appLogger,
  TrackingUtilities,
  useApiStore,
  useConfigStore,
  useErrorModalStore,
  useGenericUserStore,
} from '@nora-monorepo/ui-shared';

import { isRouteParamActive, navigateToRoute } from '#nora-ui/router/navigation';
import { RouteName } from '#nora-ui/router/route-names';
import { useDrawLayerApiStore } from '#nora-ui/stores/api/draw-layer-api-store';

export enum OperationViewMode {
  Operation = 'operation',
  GeofenceCreate = 'geofenceCreate',
  GeofenceEdit = 'geofenceEdit',
}

export enum OperationDetailsTab {
  Members = 'members',
  Geofence = 'geofence',
  Chats = 'chats',
}

interface OperationUserMetadata {
  userName: string;
  symbol?: {
    id: string;
    packId: string;
  };
}

interface OperationTrackerMetadata {
  trackerName: string;
  symbol?: {
    id: string;
    packId: string;
  };
}

export const useOperationStore = defineStore('operationStore', () => {
  const viewMode = ref<OperationViewMode>(OperationViewMode.Operation);
  const selectedDetailsTab = ref<OperationDetailsTab>(OperationDetailsTab.Members);

  const operations = ref<OperationResultDTO[]>([]);
  const currentOperation = ref<OperationResultDTO | undefined | null>();
  const currentOperationDetails = ref<OperationDetailsResultDTO | undefined>();

  const operationUserMetadata = ref<Map<string, OperationUserMetadata>>(new Map());
  const operationTrackerMetadata = ref<Map<string, OperationTrackerMetadata>>(new Map());

  const operationLoadPromises = new Map<string, Promise<void>>();

  const api = useApiStore();
  const genericUserStore = useGenericUserStore();
  const configStore = useConfigStore();
  const errorModalStore = useErrorModalStore();

  const operationsAreLoaded = ref<boolean>(false);

  const EIGHT_HOURS_IN_SECONDS: number = 60 * 60 * 8;
  const staleUserLocationTime = configStore.get('mapUi.userLocationExpirationTime', EIGHT_HOURS_IN_SECONDS) * 1000;

  const NEVER_STALE_TIMEOUT: number = 0;
  const staleTrackerLocationTime = configStore.get('mapUi.trackerLocationExpirationTime', NEVER_STALE_TIMEOUT) * 1000;

  watch(() => genericUserStore.user, (user) => {
    if (user) {
      operationUserMetadata.value.set(user.id, getOperationUserMetadata(user));
    }
  }, { immediate: true });

  async function bootstrap(): Promise<void> {
    await Promise.all([loadOperations()]);
  }

  const addOperation = (newOperation: OperationResultDTO): void => {
    const existingOperationIndex = operations.value.findIndex(({ id }) => id === newOperation.id);

    if (existingOperationIndex !== -1) {
      operations.value.splice(existingOperationIndex, 1, newOperation);

      if (currentOperation.value?.id === newOperation.id) {
        currentOperation.value = newOperation;
      }

      return;
    }

    operations.value.push(newOperation);
  };

  const eventCreateOperation = async (operation: OperationResultDTO): Promise<void> => {
    addOperation(operation);
  };

  const currentOperationUser = computed(() => {
    return (currentUserId: string): UserWithConsentNestedResultDTO | null => {
      if (!currentOperationDetails.value?.users || !currentUserId) {
        return null;
      }

      const foundUser = currentOperationDetails.value.users.find(
        user => user.user.id === currentUserId,
      );

      return foundUser ?? null;
    };
  });

  /**
   * Updates operation member in the local state, based on the event payload.
   * Since we are mutating local state, be aware that this may cause us to be out-of-sync with the backend.
   * @param newOperationMember
   */
  const eventUpdateOperationMember = async (
    newOperationMember: OperationUserDTO,
  ): Promise<void> => {
    const hasLocationScope = newOperationMember.consent?.scopes?.some(
      scope => scope.scope === ConsentScopeTypeEnum.LOCATION,
    );

    if (!hasLocationScope) {
      newOperationMember.user.lastTrack = undefined;
    }

    // If the logged-in user just gained access, fetch only the new operation.
    const loggedInUser = genericUserStore.user;

    if (
      loggedInUser !== undefined
      && loggedInUser.id === newOperationMember.user.id
      && !findOperation(newOperationMember.operationId)
    ) {
      await loadOperation(newOperationMember.operationId);
    }

    const operationIndex = operations.value.findIndex(
      ({ id }) => id === newOperationMember.operationId,
    );
    const operationExists = operationIndex !== -1;

    if (operationExists) {
      const operation = operations.value[operationIndex];
      const users = operation.users;

      if (users) {
        const userIndex = users.findIndex(
          user => user.user.id === newOperationMember.user.id,
        );

        if (userIndex !== -1) {
          users[userIndex] = newOperationMember;
        }
        else {
          users.push(newOperationMember);
        }

        // Trigger reactivity
        operations.value = [...operations.value];
      }
    }

    if (currentOperation.value?.id === newOperationMember.operationId) {
      const users = currentOperation.value.users;
      if (users) {
        const userIndex = users.findIndex(
          user => user.user.id === newOperationMember.user.id,
        );
        if (userIndex !== -1) {
          users[userIndex] = newOperationMember;
        }
        else {
          users.push(newOperationMember);
        }
      }
    }

    if (currentOperationDetails.value?.id === newOperationMember.operationId) {
      const users = currentOperationDetails.value.users;
      const userIndex = users.findIndex(
        user => user.user.id === newOperationMember.user.id,
      );
      if (userIndex !== -1) {
        currentOperationDetails.value.users[userIndex] = newOperationMember;
      }
      else {
        currentOperationDetails.value.users.push(newOperationMember);
      }
    }

    updateUserNamesMap();
  };

  /**
   * Deselect `operationId` if it is the current operation, and drop its id
   * from the URL when /operations/:operationId is showing it (the URL is left
   * alone when the user is on another route). Called by every delete/leave/
   * removed-member path, so route cleanup is traceable to the mutation that
   * caused it.
   */
  const deselectOperation = (operationId: string): void => {
    if (currentOperation.value?.id !== operationId) {
      return;
    }

    currentOperation.value = null;

    if (isRouteParamActive(RouteName.Operations, 'operationId', operationId)) {
      navigateToRoute(RouteName.Operations);
    }
  };

  const eventDeleteOperation = async (operation: OperationDeleteDTO): Promise<void> => {
    const operationIndex = operations.value.findIndex(
      ({ id }) => id === operation.id,
    );
    if (operationIndex !== -1) {
      operations.value.splice(operationIndex, 1);
    }

    deselectOperation(operation.id);

    currentOperationDetails.value = undefined;
  };

  const eventOperationRemoveMember = async (
    operationUser: OperationMemberRemoveData,
  ): Promise<void> => {
    if (genericUserStore.isCurrentUser(operationUser.userId)) {
      const removedOperationId: OperationDeleteDTO = {
        id: operationUser.operationId,
      };
      await eventDeleteOperation(removedOperationId);
    }
    const operationIndex = operations.value.findIndex(
      ({ id }) => id === operationUser.operationId,
    );

    if (operationIndex !== -1) {
      const operation = operations.value[operationIndex];
      operation.users = operation.users?.filter(
        user => user.user.id !== operationUser.userId,
      );
    }
    if (currentOperation.value?.id === operationUser.operationId && currentOperation.value.users !== undefined) {
      currentOperation.value.users = currentOperation.value.users.filter(
        user => user.user.id !== operationUser.userId,
      );
    }
    if (currentOperationDetails.value?.id === operationUser.operationId) {
      const filteredUsers = currentOperationDetails.value.users.filter(
        user => user.user.id !== operationUser.userId,
      );
      currentOperationDetails.value.users = filteredUsers;
    }
  };

  const eventUpdateUserDetails = async (
    updateUserDetails: UserDetailsUpdateEventDTO,
  ): Promise<void> => {
    if (!isNonEmptyString(updateUserDetails.userId)) {
      return;
    }

    const { operationIds, userId, customCallSign, customSymbolId, customSymbolPackId } = updateUserDetails;

    const updateCallSignAndSymbolCheck = (user: UserResultDTO) => {
      if (customCallSign !== undefined) {
        user.customCallSign = customCallSign;
      }

      if (customSymbolId !== undefined) {
        if (customSymbolId === null) {
          user.customSymbol = null;
        }
        else {
          user.customSymbol = {
            ...user.customSymbol,
            id: customSymbolId,
            symbolPackId: customSymbolPackId ?? '',
          } as SymbolDTO;
        }
      }
    };
    for (const operationId of operationIds) {
      const operation = findOperation(operationId);
      const userIndex = operation?.users?.findIndex(user => user.user.id === userId);

      if (operation?.users && userIndex !== undefined && userIndex !== -1) {
        const operationUser = operation.users[userIndex];
        updateCallSignAndSymbolCheck(operationUser.user);
      }

      if (currentOperation.value?.id === operationId && currentOperation.value.users !== undefined) {
        const userIndex = currentOperation.value.users.findIndex(user => user.user.id === userId);
        if (userIndex !== -1) {
          const currentOperationUser = currentOperation.value.users[userIndex];
          updateCallSignAndSymbolCheck(currentOperationUser.user);
        }
      }

      if (currentOperationDetails.value?.id === operationId) {
        const userIndex = currentOperationDetails.value.users.findIndex(user => user.user.id === userId);
        if (userIndex !== -1) {
          const currentOperationDetailUser = currentOperationDetails.value.users[userIndex];
          updateCallSignAndSymbolCheck(currentOperationDetailUser.user);
        }
      }
    };

    // Trigger reactivity for operations array
    operations.value = [...operations.value];

    updateUserNamesMap();
  };

  /**
   * Updates the local roles of given operations, based on the event payload.
   */
  // TODO: Unshittify this monster of a function.
  const eventUpdateOperationLocalRoles = async (eventData: OperationLocalRoleUpdatedDTO[]) => {
    for (const { userId, operationId, localRoleId } of eventData) {
      appLogger.debug({
        message: `Updating local role for user ${userId} in operation ${operationId} to ${localRoleId}`,
      });
      const operationIndex = operations.value.findIndex(
        ({ id }) => id === operationId,
      );
      if (operationIndex !== -1) {
        const users = operations.value[operationIndex].users;
        if (users) {
          const userIndex = users.findIndex(
            user => user.user.id === userId,
          );
          if (userIndex !== -1) {
            users[userIndex].localRole.id = localRoleId;
          }
        }
      }
      if (currentOperation.value?.id === operationId && currentOperation.value?.users) {
        const userIndex = currentOperation.value.users.findIndex(
          user => user.user.id === userId,
        );
        if (userIndex !== -1) {
          currentOperation.value.users[userIndex].localRole.id = localRoleId;
        }
      }
      if (currentOperationDetails.value?.id === operationId) {
        const userIndex = currentOperationDetails.value.users.findIndex(
          user => user.user.id === userId,
        );
        if (userIndex !== -1) {
          currentOperationDetails.value.users[userIndex].localRole.id = localRoleId;
        }
      }
    }
  };

  /**
   * Updates the operation name in the local state of the specified operation.
   */
  const eventUpdateOperationName = async ({ operationId, newName }: { newName: string; operationId: string }): Promise<void> => {
    const operationIndex = operations.value.findIndex(
      ({ id }) => id === operationId,
    );
    if (operationIndex !== -1) {
      operations.value[operationIndex].name = newName;
    }
    if (currentOperation.value?.id === operationId) {
      currentOperation.value.name = newName;
    }
    if (currentOperationDetails.value?.id === operationId) {
      currentOperationDetails.value.name = newName;
    }
  };

  const eventUpdateOperation = async (operation: OperationResultDTO): Promise<void> => {
    const operationIndex = operations.value.findIndex(
      ({ id }) => id === operation.id,
    );
    if (operationIndex !== -1) {
      operations.value.splice(operationIndex, 1, operation);
    }
    else {
      addOperation(operation);
    }
    if (currentOperation.value?.id === operation.id) {
      currentOperation.value = operation;
    }
    if (currentOperationDetails.value?.id === operation.id) {
      await updateCurrentOperationDetails();
    }
  };

  const eventUpdateOperationConversationList = async (operationId: string): Promise<void> => {
    if (currentOperationDetails.value?.id === operationId) {
      await updateCurrentOperationDetails();
    }
  };

  async function fetchOperationDetails(operationId: string): Promise<GetOperationDetails200Response> {
    const details = await api.unwrap(
      api.nexusOperation.getOperationDetails({
        operationId,
      }),
    );

    if (!details.data) {
      throw new Error('no operations returned');
    }

    return details;
  }

  async function loadOperation(operationId: string): Promise<void> {
    if (findOperation(operationId)) {
      return;
    }

    const existingLoad = operationLoadPromises.get(operationId);
    if (existingLoad) {
      return await existingLoad;
    }

    const operationLoad = (async () => {
      const operationDetails = await fetchOperationDetails(operationId);
      if (!operationDetails.data) {
        throw new Error('Operation details not found');
      }

      addOperation(operationDetails.data);
      updateUserNamesMap();
      updateTrackerMetadataMap();
    })();

    operationLoadPromises.set(operationId, operationLoad);

    try {
      await operationLoad;
    }
    finally {
      operationLoadPromises.delete(operationId);
    }
  }

  async function updateCurrentOperationDetails(): Promise<void> {
    const operationId = currentOperationDetails.value?.id;

    if (!isNonEmptyString(operationId)) {
      return;
    }

    const newCurrentOperationData = await fetchOperationDetails(operationId);
    currentOperationDetails.value = newCurrentOperationData.data;
  }

  const eventCreateTracker = async (
    createdTracker: OperationTrackerDetailsEventDTO,
  ): Promise<void> => {
    if (!createdTracker.operationIds || createdTracker.operationIds.length === 0) {
      return;
    }

    for (const operationId of createdTracker.operationIds) {
      const operation = findOperation(operationId);

      if (operation) {
        if (!operation.trackers) {
          operation.trackers = [createdTracker];
        }
        else if (!operation.trackers.some(({ id }) => id === createdTracker.id)) {
          operation.trackers.push(createdTracker);
        }
      }

      if (currentOperation.value?.id === operationId) {
        if (!currentOperation.value.trackers) {
          currentOperation.value.trackers = [createdTracker];
        }
        else if (!currentOperation.value.trackers.some(({ id }) => id === createdTracker.id)) {
          currentOperation.value.trackers.push(createdTracker);
        }
      }

      if (currentOperationDetails.value?.id === operationId) {
        if (!currentOperationDetails.value.trackers.some(({ id }) => id === createdTracker.id)) {
          currentOperationDetails.value.trackers.push(createdTracker);
        }
      }
    }

    updateTrackerMetadataMap();
  };

  function updateOrAddTracker(trackers: OperationTrackerDetailsDTO[], updatedTracker: OperationTrackerDetailsEventDTO): void {
    const trackerIndex = trackers.findIndex(({ id }) => id === updatedTracker.id);
    if (trackerIndex !== -1) {
      trackers[trackerIndex] = updatedTracker;
    }
    else {
      trackers.push(updatedTracker);
    }
  }

  const eventUpdateTracker = async (updatedTracker: OperationTrackerDetailsEventDTO): Promise<void> => {
    if (!updatedTracker.operationIds || updatedTracker.operationIds.length === 0) {
      return;
    }

    for (const operationId of updatedTracker.operationIds) {
      const operation = findOperation(operationId);

      if (operation?.trackers) {
        updateOrAddTracker(operation.trackers, updatedTracker);
      }

      if (currentOperation.value?.id === operationId && currentOperation.value.trackers) {
        updateOrAddTracker(currentOperation.value.trackers, updatedTracker);
      }

      if (currentOperationDetails.value?.id === operationId) {
        updateOrAddTracker(currentOperationDetails.value.trackers, updatedTracker);
      }
    }

    // Trigger reactivity for operations array
    operations.value = [...operations.value];

    updateTrackerMetadataMap();
  };

  const eventDeleteTracker = async (
    deletedTracker: TrackerDeleteDTO,
  ): Promise<void> => {
    for (const operation of operations.value) {
      if (operation.trackers) {
        operation.trackers = operation.trackers.filter(
          tracker => tracker.id !== deletedTracker.id,
        );
      }
    }

    if (currentOperation.value?.trackers) {
      currentOperation.value.trackers = currentOperation.value.trackers.filter(
        tracker => tracker.id !== deletedTracker.id,
      );
    }

    if (currentOperationDetails.value?.trackers) {
      currentOperationDetails.value.trackers = currentOperationDetails.value.trackers.filter(
        tracker => tracker.id !== deletedTracker.id,
      );
    }
  };

  async function loadOperations(): Promise<void> {
    const myOperations = await api.unwrap(api.nexusOperation.getMyOperations());

    if (!myOperations.data) {
      throw new Error('no operations returned');
    }

    operations.value = myOperations.data;

    updateUserNamesMap();
    updateTrackerMetadataMap();

    operationsAreLoaded.value = true;
  }

  function updateUserNamesMap(): void {
    const newOperationUsers = new Map(operationUserMetadata.value);

    for (const operation of operations.value) {
      for (const { user } of operation.users || []) {
        newOperationUsers.set(
          user.id,
          getOperationUserMetadata(user),
        );
      }
    }

    operationUserMetadata.value = newOperationUsers;
  }

  function updateTrackerMetadataMap(): void {
    const newOperationTrackers = new Map(operationTrackerMetadata.value);

    for (const operation of operations.value) {
      for (const tracker of operation.trackers || []) {
        newOperationTrackers.set(
          tracker.id,
          getOperationTrackerMetadata(tracker),
        );
      }
    }

    operationTrackerMetadata.value = newOperationTrackers;
  }

  function getOperationUserMetadata(user: UserResultDTO): OperationUserMetadata {
    const metadata: OperationUserMetadata = {
      userName: getUserDisplayName(user),
    };

    if (isNonEmptyString(user.customSymbol?.id) && isNonEmptyString(user.customSymbol.symbolPackId)) {
      metadata.symbol = {
        id: user.customSymbol.id,
        packId: user.customSymbol.symbolPackId,
      };
    }

    return metadata;
  }

  function getOperationTrackerMetadata(tracker: OperationTrackerDetailsDTO): OperationTrackerMetadata {
    const metadata: OperationTrackerMetadata = {
      trackerName: tracker.name,
    };

    if (isNonEmptyString(tracker.customSymbol?.id) && isNonEmptyString(tracker.customSymbol.symbolPackId)) {
      metadata.symbol = {
        id: tracker.customSymbol.id,
        packId: tracker.customSymbol.symbolPackId,
      };
    }

    return metadata;
  }

  function findOperation(operationId: string): OperationResultDTO | null {
    const operation = operations.value.find(
      operation => operation.id === operationId,
    );
    if (!operation) {
      return null;
    }
    return operation;
  };

  const validateRemainingAdmins = async (
    operationId: string,
    userIdToRemove: string,
  ): Promise<boolean> => {
    const operationDetails = await fetchOperationDetails(operationId);

    const remainingAdmins = operationDetails.data?.users.filter(
      user => user.localRole.defaultAdmin && user.user.id !== userIdToRemove,
    );

    if (remainingAdmins?.length === 0) {
      errorModalStore.showErrorModal({
        headerText: 'No Admins Found',
        bodyText: 'At least one admin must be assigned to the operation.',
      });
      return false;
    }

    return true;
  };

  const leaveOperation = async (operationId: string): Promise<void> => {
    const loggedInUser = genericUserStore.user;
    if (!loggedInUser) {
      throw new Error('User not logged in');
    }
    const canLeave = await validateRemainingAdmins(
      operationId,
      loggedInUser.id,
    );
    if (!canLeave) {
      return;
    }

    await api.nexusOperation.leaveOperation({ operationId });
    operations.value = operations.value.filter(
      operation => operation.id !== operationId,
    );

    deselectOperation(operationId);
  };

  const removeUserFromOperation = async (
    operationId: string,
    userId: string,
  ): Promise<void> => {
    const canRemove = await validateRemainingAdmins(operationId, userId);
    if (!canRemove) {
      return;
    }
    await api.nexusOperation.removeUserFromOperation({ operationId, userId });
  };

  const deleteOperation = async (operationId: string): Promise<void> => {
    await api.nexusOperation.deleteOperation({ operationId });
    operations.value = operations.value.filter(
      operation => operation.id !== operationId,
    );

    deselectOperation(operationId);

    if (!currentOperationDetails.value) {
      throw new Error('Current operation details not found');
    }
    if (currentOperationDetails.value.id === operationId) {
      currentOperationDetails.value = undefined;
    }
  };

  const getOperation = computed(() => {
    return (operationId?: string): OperationResultDTO | undefined => {
      if (!isNonEmptyString(operationId)) {
        return undefined;
      }

      return operations.value.find(operation => operation.id === operationId);
    };
  });

  /**
   * The draw layers of an operation, fetched from the API. Falls back to the draw layers
   * bundled with the operation DTO (bootstrap data) only when the fetch fails (`null`),
   * so the map can still render draw layers while offline. An empty array is a valid
   * result (e.g. after the operation's last layer was deleted) and must not resurrect
   * stale bootstrap layers. The fallback layers omit operationId, so it is stamped on
   * every returned layer.
   */
  async function fetchOperationDrawLayers(operationId: string): Promise<DrawLayerResponseDTO[]> {
    const drawLayerApiStore = useDrawLayerApiStore();
    const fetchedDrawLayers = await drawLayerApiStore.getDrawLayersByOperationId(operationId);

    const drawLayers = fetchedDrawLayers ?? getOperation.value(operationId)?.drawLayers ?? [];

    return drawLayers.map(drawLayer => ({ ...drawLayer, operationId }));
  }

  const getOperationUsers = (operationId: string): UserWithConsentAndLocalRoleDTO[] => {
    const operation = findOperation(operationId);

    if (!operation?.users) {
      return [];
    }

    return operation.users;
  };

  function isUserSharingLocationInOperation(userId: string, operationId: string): boolean {
    const operationUsers = getOperationUsers(operationId);
    for (const operationUser of operationUsers) {
      if (operationUser.user.id !== userId) {
        continue;
      }

      const hasLocationScope = operationUser.consent.scopes.some(scope => scope.scope === ConsentScopeTypeEnum.LOCATION);
      if (!hasLocationScope) {
        return false;
      }
    }
    return true;
  }

  const getOperationTrackers = (operationId: string): OperationTrackerDetailsDTO[] => {
    const operation = findOperation(operationId);
    if (!operation?.trackers) {
      return [];
    }

    return operation.trackers;
  };

  const handleIncomingUserTrack = (userId: string, position: PositionResponse): void => {
    for (const operation of operations.value) {
      const operationUsers = operation.users;
      if (operationUsers) {
        operationUsers.map((user) => {
          if (user.user.id === userId) {
            user.user.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);
          }
          return user;
        });
      }
    };

    if (currentOperation.value?.users) {
      currentOperation.value.users = currentOperation.value.users.map((user) => {
        if (user.user.id === userId) {
          user.user.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);

          return user;
        }
        return user;
      });
    }

    if (currentOperationDetails.value?.users) {
      currentOperationDetails.value.users = currentOperationDetails.value.users.map((user) => {
        if (user.user.id === userId) {
          user.user.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);
        }
        return user;
      });
    }
  };

  const clearUserLastTrack = (userId: string, operationId?: string): void => {
    const clearFromUsers = (users: Array<{ user: UserResultDTO }> | undefined): void => {
      for (const { user } of users ?? []) {
        if (user.id === userId) {
          user.lastTrack = undefined;
        }
      }
    };

    for (const operation of operations.value) {
      if (operationId !== undefined && operation.id !== operationId) {
        continue;
      }

      clearFromUsers(operation.users);
    }

    if (operationId === undefined || currentOperation.value?.id === operationId) {
      clearFromUsers(currentOperation.value?.users);
    }

    if (operationId === undefined || currentOperationDetails.value?.id === operationId) {
      clearFromUsers(currentOperationDetails.value?.users);
    }
  };

  const handleIncomingTrackerTrack = (trackerId: string, position: PositionResponse): void => {
    for (const operation of operations.value) {
      const operationTrackers = operation.trackers;
      if (operationTrackers) {
        operationTrackers.map((tracker) => {
          if (tracker.id === trackerId) {
            tracker.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);
          }
          return tracker;
        });
      }
    }

    if (currentOperation.value?.trackers) {
      currentOperation.value.trackers = currentOperation.value.trackers.map((tracker) => {
        if (tracker.id === trackerId) {
          tracker.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);
        }
        return tracker;
      });
    }

    if (currentOperationDetails.value?.trackers) {
      currentOperationDetails.value.trackers = currentOperationDetails.value.trackers.map((tracker) => {
        if (tracker.id === trackerId) {
          tracker.lastTrack = TrackingUtilities.positionResponseToTrackDTO(position);
        }
        return tracker;
      });
    }
  };

  const getStaleOperationUserLocationIds = (): string[] => {
    const userLocationsToRemove: string[] = [];
    for (const operation of operations.value) {
      for (const user of operation.users ?? []) {
        const userTrackTime = ensureValidDate(user.user.lastTrack?.serverTimestamp)?.getTime();
        const lastAcceptableUserTrackTime = Date.now() - staleUserLocationTime;

        if (!user.user.lastTrack || !isNonZero(userTrackTime)) {
          continue;
        }

        if (userTrackTime <= lastAcceptableUserTrackTime) {
          user.user.lastTrack = undefined;
          userLocationsToRemove.push(user.user.id);
        }
      }
    }

    return userLocationsToRemove;
  };

  const getStaleOperationTrackerLocationIds = (): string[] => {
    if (staleTrackerLocationTime === 0) {
      return [];
    }
    const trackerLocationsToRemove: string[] = [];
    for (const operation of operations.value) {
      for (const tracker of operation.trackers ?? []) {
        const lastTrackerTrackTime = ensureValidDate(tracker.lastTrack?.serverTimestamp)?.getTime();
        const lastAcceptableTrackerTime = Date.now() - staleTrackerLocationTime;

        if (!tracker.lastTrack || !isNonZero(lastTrackerTrackTime)) {
          continue;
        }

        if (lastTrackerTrackTime <= lastAcceptableTrackerTime) {
          tracker.lastTrack = undefined;
          trackerLocationsToRemove.push(tracker.id);
        }
      }
    }

    return trackerLocationsToRemove;
  };

  const getOperationPermissions = (operationId: string): string[] => {
    if (!currentOperationDetails.value) {
      return [];
    }

    if (operationId !== currentOperationDetails.value.id) {
      return [];
    }

    return currentOperationDetails.value.permissions || [];
  };

  function isUserInOperation(userId: string, operationId: string): boolean {
    const operation = operations.value.find(op => op.id === operationId);

    if (!operation) {
      return false;
    }

    return operation.users?.some(operationUser => operationUser.user.id === userId) ?? false;
  }

  function isTrackerInOperation(trackerId: string, operationId: string): boolean {
    const operation = operations.value.find(op => op.id === operationId);

    if (!operation) {
      return false;
    }

    return operation.trackers?.some(tracker => tracker.id === trackerId) ?? false;
  }

  return {
    currentOperation,
    currentOperationDetails,
    currentOperationUser,
    getOperation,
    operationsAreLoaded,
    operations,
    operationUserMetadata,
    operationTrackerMetadata,
    viewMode,
    selectedDetailsTab,
    bootstrap,
    addOperation,
    eventCreateOperation,
    eventUpdateOperation,
    eventUpdateOperationMember,
    eventUpdateOperationName,
    eventUpdateOperationLocalRoles,
    eventOperationRemoveMember,
    eventUpdateOperationConversationList,
    eventUpdateUserDetails,
    eventDeleteOperation,
    eventCreateTracker,
    eventUpdateTracker,
    eventDeleteTracker,
    fetchOperationDrawLayers,
    findOperation,
    getOperationUsers,
    getOperationTrackers,
    getOperationPermissions,
    loadOperation,
    loadOperations,
    leaveOperation,
    removeUserFromOperation,
    deleteOperation,
    clearUserLastTrack,
    handleIncomingUserTrack,
    handleIncomingTrackerTrack,
    getStaleOperationUserLocationIds,
    getStaleOperationTrackerLocationIds,
    isUserInOperation,
    isUserSharingLocationInOperation,
    isTrackerInOperation,
    staleUserLocationTime,
    staleTrackerLocationTime,
  };
});
